import React, { useMemo, useState } from "react";
import { SketchPicker } from "react-color"; // 调色板组件

/**
 * Props
 * - OriginalTheme: 可能包含 configuration / colors / legacy color 等
 * - onChange: 颜色变更回调；当 fieldKey==='primaryColor' 时回调 { primaryColor: hex }，否则回调 hex 字符串
 * - fieldKey: 正在编辑的字段键名（'primaryColor' | 'topBannerBackground' 等），默认 'primaryColor'
 * - presetColors: 预设颜色列表，传给 SketchPicker 的 presetColors
 * - fieldLabel: 面板标题文案，默认 'Banner Colour'（保留你现有 UI）
 */
import { describeColor } from "../themeEditor/utils";

function ColorPalette({
  OriginalTheme,
  onChange,
  fieldKey = "primaryColor",
  presetColors = [],
  fieldLabel = "Banner Colour",
}) {
  // 读取时如果没有 primaryColor/secondaryColor，就回退到旧的 color
  // 中文：初始颜色按当前编辑的 fieldKey 读取；primary 优先读 configuration.primaryColor；banner 读 configuration.topBannerBackground。
  // EN: Initial color respects the active fieldKey; primary reads from configuration.primaryColor; banner reads configuration.topBannerBackground.
  const initialPrimaryColor = useMemo(() => {
    const cfg = OriginalTheme?.configuration || {};
    if (fieldKey === "primaryColor") {
      return (
        cfg.primaryColor ||
        OriginalTheme?.primaryColor ||
        OriginalTheme?.color ||
        "#2003fc"
      );
    }
    if (fieldKey === "topBannerBackground") {
      return (
        cfg.topBannerBackground ||
        OriginalTheme?.topBannerBackground ||
        OriginalTheme?.color ||
        "#2003fc"
      );
    }
    // 其它字段默认兼容旧逻辑
    return (
      cfg?.[fieldKey] ||
      OriginalTheme?.[fieldKey] ||
      OriginalTheme?.primaryColor ||
      OriginalTheme?.color ||
      "#2003fc"
    );
  }, [OriginalTheme, fieldKey]);

  // const initialSecondaryColor = OriginalTheme.secondaryColor || "#ffffff";
  // const initialFontColor = OriginalTheme.fontColor || "#000000";

  const [primaryColor, setPrimaryColor] = useState(initialPrimaryColor);
  // const [secondaryColor, setSecondaryColor] = useState(initialSecondaryColor);
  // const [fontColor, setFontColor] = useState(initialFontColor);


  // 当主色变更
  const handlePrimaryColorChange = (colorResult) => {
    const hex = colorResult.hex;
    setPrimaryColor(hex);

    // 兼容你现有页面的 handleThemeChange：
    // - 编辑 primaryColor → 回传 { primaryColor: hex }
    // - 其它（如 banner） → 回传字符串 hex
    if (fieldKey === "primaryColor") {
      onChange && onChange({ primaryColor: hex });
    } else {
      onChange && onChange(hex);
    }
  };

  // 当次色变更
  // const handleSecondaryColorChange = (colorResult) => {
  //   const hex = colorResult.hex;
  //   setSecondaryColor(hex);
  //   onChange && onChange({ primaryColor, secondaryColor: hex, fontColor });
  // };

  // 字体色选项
  // const fontColorOptions = [
  //   { value: "#000000", label: "Black" },
  //   { value: "#1B1B1B", label: "Light Black" },
  //   { value: "#363636", label: "Darker Grey" },
  //   { value: "#515151", label: "Grey" }
  // ];

  // 当字体色变更
  // const handleFontColorChange = (e) => {
  //   const val = e.target.value;
  //   setFontColor(val);
  //   onChange && onChange({ primaryColor, secondaryColor, fontColor: val });
  // };

  return (
    <div className="px-8 bg-white border-l border-gray-100 rounded-md mb-10">
      {/* 主色 */}
      <label className="block text-md font-bold text-gray-700 mb-2">
        {fieldLabel}
      </label>
      <div className="grid grid-cols-3 gap-4 items-center mt-4">
        <div className="col-span-2 flex justify-center">
          <SketchPicker
            color={primaryColor}
            onChangeComplete={handlePrimaryColorChange}
            disableAlpha
            className="shadow-lg"
            // 预设色（可选）：如果页面传入，显示在取色器底部；不传则维持默认行为
            presetColors={presetColors}
          />
        </div>
        <div className="col-span-1 flex flex-col items-center justify-center gap-3">
          <div
            className="w-16 h-16 rounded-full border border-gray-300 shadow-md"
            style={{ backgroundColor: primaryColor }}
          />
          <div className="text-center">
            <div className="text-sm font-semibold text-[#334155]">
              {describeColor(primaryColor)}
            </div>
            <div className="mt-1 text-xs text-[#94a3b8]">
              {primaryColor?.toUpperCase?.() || ""}
            </div>
          </div>
        </div>
      </div>

      {/* 次色
      <label className="block text-md font-bold text-gray-700 mb-2 mt-6">
        Font Colour
      </label>
      <div className="grid grid-cols-3 gap-4 items-center mt-4">
        <div className="col-span-2 flex justify-center">
          <SketchPicker
            color={secondaryColor}
            onChangeComplete={handleSecondaryColorChange}
            disableAlpha
            className="shadow-lg"
          />
        </div>
        <div className="col-span-1 flex justify-center">
          <div
            className="w-16 h-16 rounded-full border border-gray-300 shadow-md"
            style={{ backgroundColor: secondaryColor }}
          />
        </div>
      </div> */}

      {/* 字体色 */}
      {/* <label className="block text-md font-bold text-gray-700 mb-2 mt-6">
        Page Font Colour
      </label>
      <div className="grid grid-cols-3 gap-4 mt-4">
        <div className="col-span-2 flex justify-center">
          <select
            value={fontColor}
            onChange={handleFontColorChange}
            className="border border-gray-300 rounded px-2 w-40 h-10"
          >
            {fontColorOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-1 flex justify-center">
          <div
            className="w-16 h-16 rounded-full border border-gray-300 shadow-md"
            style={{ backgroundColor: fontColor }}
          />
        </div>
      </div> */}
    </div>
  );
}

export default ColorPalette;
